/**
 * Las entregas que van llegando de los conductores.
 *
 * 🚨 Esta pantalla cierra el círculo. Hasta ahora el conductor registraba la
 * entrega con su foto y su firma, y en la oficina **no había dónde verla**. Sin
 * esto no se puede hacer el piloto: nadie sabría si el sistema está recibiendo
 * algo, ni podría revisar una entrega cuando el cliente reclame.
 *
 * Lo que la oficina necesita responder de un vistazo es: qué llegó, quién
 * recibió, y **si la evidencia está completa**. Por eso la columna de evidencia
 * va marcada en rojo cuando falta algo — es el único uso de rojo permitido.
 *
 * 🚨 SIN UN SOLO PESO, igual que todo lo demás.
 */

import { useCallback, useEffect, useState } from 'react'
import { usarPaginas, PieDeTabla } from './Tabla.tsx'
import { puedeCambiar, tokenGuardado } from './Entrar.tsx'
import { VerFotos } from './VerFotos.tsx'
import { salirDeLaConsola, pedirConSesion, quienEntro } from './Entrar.tsx'
import { pesoLegible } from '@symtrack/shared'

/**
 * ¿A esta entrega le falta que alguien la mire?
 *
 * 🚨 Son DOS casos y es fácil contar de más: la que nadie ha revisado, y la que
 * se devolvió y el conductor **ya corrigió** —esa vuelve a la fila—. Una
 * devuelta que sigue esperando al conductor NO cuenta: ya se revisó, la pelota
 * está del otro lado.
 *
 * Vive aquí porque lo usan la etiqueta de la fila y la cola del panel. Cuando
 * la cola contaba distinto que la lista, el botón decía «Aprobar y seguir» con
 * una sola pendiente.
 */
/**
 * ¿Esta entrega trae el punto donde se hizo?
 *
 * ⛔ Rechaza el 0,0 igual que la app: el navegador a veces responde por el
 * camino bueno con «latitud 0, longitud 0», que es un sitio en el mar. Guardar
 * o mostrar eso sería peor que no tener nada.
 */
function hayPunto(e: Entrega): boolean {
  const lat = Number(e.lat)
  const lng = Number(e.lng)
  if (e.lat == null || e.lng == null) return false
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
  return !(lat === 0 && lng === 0)
}

/**
 * ¿A esta entrega le falta la hoja del cumplido?
 *
 * 🚨 Antes esto era «sin foto o sin firma», y desde el 22-ago la firma **ya no
 * se pide** —Luis: «las personas que nos reciben no nos firman nada»—. Con el
 * criterio viejo, TODAS las entregas nuevas caían en ese aviso rojo para
 * siempre, por algo que dejó de ser un problema. Un aviso que sale en todo
 * enseña a ignorar los avisos.
 *
 * ⭐ Y lo que queda es lo que de verdad le importa a la oficina: **sin esa hoja
 * no se le factura a la empresa matriz.** Ahora la hoja tampoco frena al
 * conductor —el cliente a veces la entrega días después—, así que perseguir
 * las que faltan es trabajo real de todos los días.
 */
function leFaltaLaHoja(e: Entrega): boolean {
  /* ⭐ Por REMESA, igual que la app del conductor: el cumplido es de la remesa,
     y si él lo mandó después la remesa ya lo tiene. Contando por entrega, las
     dos pantallas decían números distintos sobre lo mismo. */
  return Number(e.hojas_remesa ?? 0) === 0
}

/**
 * ¿A esta entrega le falta que la oficina la apruebe?
 *
 * 🚨 MISMO CRITERIO QUE EL TABLERO Y QUE LA APP DEL CONDUCTOR. Antes había
 * TRES criterios distintos para la misma pregunta, y MEDIDO el 24-ago-2026
 * daban tres números: el tablero 26, la lista 23, y esta marca 27.
 *
 * ⭐ La pelota es de la oficina salvo que la remesa esté en DEVUELTA — que es
 * exactamente el dato con el que la app del conductor decide si le toca a él.
 * Cuando corrige, `corregir.ts` devuelve la remesa a ENTREGADA. Si la oficina y
 * el conductor no coinciden en quién tiene la pelota, nadie la mueve.
 */
function faltaRevisarla(e: Entrega): boolean {
  if (e.estado_evidencia === 'DEVUELTA') return false
  return e.revision !== 'APROBADA'
}

interface Entrega {
  entrega_id: string
  remesa: string
  numero_manifiesto: string | null
  placa: string | null
  conductor_cedula: string | null
  conductor_nombre: string | null
  destino: string | null
  /** «Cartagena, Bolívar» — sale del código DANE que manda Vulcano. */
  ciudad_destino?: string | null
  peso_ton: string | null
  estado_evidencia: string
  cliente: string | null
  recibe_nombre: string | null
  recibe_cedula: string | null
  entregada_at: string | null
  /** Dónde se hizo la entrega. Viene vacío cuando el GPS no respondió. */
  lat?: number | string | null
  lng?: number | string | null
  observacion: string | null
  tiene_foto: string | number
  tiene_firma: string | number
  /** Cuántas hojas del cumplido tiene la REMESA, contando todas sus entregas. */
  hojas_remesa?: number | string | null
  hojas_papel: string | number
  empezado_at: string | null
  codigo_cumplido: string
  revision: string | null
  motivo_devolucion: string | null
  /** Mandó evidencia después de que se la devolvieran: hay que volver a mirarla. */
  corregida_despues: boolean | null
}

/** Una fila de la tabla: o una entrega, o un viaje que no pudo entregar. */
type FilaTabla =
  | { clase: 'entrega'; cuando: string | null; e: Entrega }
  | { clase: 'fallido'; cuando: string | null; n: NoEntregado }

interface NoEntregado {
  remesa: string
  placa: string | null
  conductor_cedula: string | null
  conductor_nombre: string | null
  destino: string | null
  cliente: string | null
  veces: string | number
  ultimo_intento: string | null
  motivo: string | null
  detalle: string | null
  /* Lo que respondió el cliente desde su portal. */
  espera_cliente: boolean | null
  decision_cliente: string | null
  decision_nota: string | null
  decision_at: string | null
  /* Para poder darle razón al conductor desde esta misma fila. */
  novedad_id: string | null
  dijo_el_conductor: string | null
  razon_oficina: string | null
  razon_oficina_nota: string | null
  razon_oficina_at: string | null
}

/**
 * Qué le puede decir la oficina al conductor.
 *
 * ⭐ Las tres primeras son las MISMAS que le llegan del cliente, para que en su
 * pantalla la instrucción se lea igual venga de quien venga. La cuarta es
 * propia de la oficina.
 */
const RAZONES: [string, string, string][] = [
  ['REPROGRAMAR', 'Que vuelva a intentar', 'Mismo destino, otro día'],
  ['CAMBIAR_DESTINO', 'Que la entregue en otra parte', 'Escribe abajo la dirección'],
  ['DEVOLVER', 'Que la devuelva a planta', 'La carga se regresa'],
  ['ESPERA', 'Que espere ahí', 'Le avisamos nosotros'],
]

/** Lo que el conductor dijo que hizo con la carga. */
const DIJO_EL_CONDUCTOR: Record<string, string> = {
  VUELVO_A_INTENTAR: 'Se quedó con la carga para volver a intentar',
  DEVUELVO_A_PLANTA: 'La devolvió a planta',
  ESPERO_OFICINA: '⚠ Quedó esperando razón nuestra',
}

/** Lo que el cliente pidió que hiciéramos, dicho como una instrucción. */
const PIDIO_EL_CLIENTE: Record<string, string> = {
  REPROGRAMAR: 'Volver a intentar',
  CAMBIAR_DESTINO: 'Entregar en otra parte',
  DEVOLVER: 'Devolver a planta',
  COORDINO_YO: 'Él lo coordina y avisa',
}

/** Los motivos, dichos como los diría alguien. */
const MOTIVO_EN_PALABRAS: Record<string, string> = {
  NADIE_RECIBIO: 'No había nadie / estaba cerrado',
  SIN_QUIEN_DESCARGUE: 'No había quien descargara',
  CLIENTE_RECHAZO: 'El cliente no recibió la carga',
  DIRECCION_ERRADA: 'La dirección no era',
  VEHICULO_VARADO: 'El camión se varó',
  OTRO: 'Otra cosa',
}

/** Por qué se devuelve una entrega. Dicho como lo diría alguien. */
const MOTIVOS_DEVOLUCION = [
  { valor: 'FOTO_ILEGIBLE', texto: 'La foto no se lee' },
  { valor: 'FALTA_SELLO', texto: 'Falta el sello del cliente' },
  { valor: 'FIRMA_SIN_CEDULA', texto: 'Firmaron sin poner la cédula' },
  { valor: 'GPS_FUERA_DE_RANGO', texto: 'El punto no cuadra con el destino' },
  { valor: 'CANTIDAD_INCONSISTENTE', texto: 'La cantidad no cuadra' },
  { valor: 'DOCUMENTO_EQUIVOCADO', texto: 'Es el documento equivocado' },
  { valor: 'FALTA_EIR', texto: 'Falta el EIR' },
  { valor: 'OTRO', texto: 'Otra cosa' },
] as const

/**
 * 🚨 Los atajos de fecha, y ESTA VEZ mostrando qué fecha aplican.
 *
 * Luis, el 21-ago-2026:
 *   «Me ponen unos filtros: 7 días, 30 días, 90 días. ¿Quién dijo dónde están
 *    las fechas, dónde están las cosas?»
 *
 * ⭐ Un botón que dice «30 días» obliga a hacer la cuenta de cabeza. Uno que
 * dice «Este mes · 1 – 21 ago» ya la hizo. Y al lado quedan Desde y Hasta con
 * calendario, para pedir cualquier rango.
 */
const HOY_ISO = () => new Date().toLocaleDateString('sv-SE') // sv-SE da AAAA-MM-DD
function isoDe(d: Date): string {
  return d.toLocaleDateString('sv-SE')
}
function atajos(): { texto: string; desde: string; hasta: string }[] {
  const hoy = new Date()
  const ayer = new Date(hoy); ayer.setDate(hoy.getDate() - 1)
  // Lunes de esta semana (en Colombia la semana arranca el lunes).
  const lunes = new Date(hoy); lunes.setDate(hoy.getDate() - ((hoy.getDay() + 6) % 7))
  const primeroDeMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
  const finMesPasado = new Date(hoy.getFullYear(), hoy.getMonth(), 0)
  const primeroMesPasado = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1)
  return [
    { texto: 'Hoy', desde: isoDe(hoy), hasta: isoDe(hoy) },
    { texto: 'Ayer', desde: isoDe(ayer), hasta: isoDe(ayer) },
    { texto: 'Esta semana', desde: isoDe(lunes), hasta: isoDe(hoy) },
    { texto: 'Este mes', desde: isoDe(primeroDeMes), hasta: isoDe(hoy) },
    { texto: 'Mes pasado', desde: isoDe(primeroMesPasado), hasta: isoDe(finMesPasado) },
  ]
}
/** «1 – 21 ago» · «21 ago». Lo que va debajo del nombre del atajo. */
function comoSeLee(desde: string, hasta: string): string {
  const d = new Date(desde + 'T12:00:00')
  const h = new Date(hasta + 'T12:00:00')
  const mes = (f: Date) => f.toLocaleDateString('es-CO', { month: 'short' }).replace('.', '')
  if (desde === hasta) return `${d.getDate()} ${mes(d)}`
  if (d.getMonth() === h.getMonth()) return `${d.getDate()} – ${h.getDate()} ${mes(h)}`
  return `${d.getDate()} ${mes(d)} – ${h.getDate()} ${mes(h)}`
}

export function Entregas() {
  const [entregas, setEntregas] = useState<Entrega[] | null>(null)
  // Arranca en este mes: es el rango que la oficina mira todos los días.
  const [desde, setDesde] = useState(() => {
    const h = new Date()
    return isoDe(new Date(h.getFullYear(), h.getMonth(), 1))
  })
  const [hasta, setHasta] = useState(HOY_ISO)
  const [busca, setBusca] = useState('')
  /*
   * 🚨 Filtrar por cliente y por en qué va la revisión. Lo pidió Luis: «dónde
   * está para ver por filtro, por cliente, por esto». Con 37 clientes rodando
   * al mismo tiempo, encontrar el de uno solo a ojo era imposible.
   */
  const [cliente, setCliente] = useState('')
  const [estado, setEstado] = useState('')
  const [clientes, setClientes] = useState<{ id: string; nombre: string; en_calle: number; entregas: number }[]>([])
  const [noEntregados, setNoEntregados] = useState<NoEntregado[]>([])
  /*
   * Cuando está puesto, la ÚNICA tabla muestra solo los viajes que no pudieron
   * entregar. Reemplaza a la segunda tabla que había antes.
   */
  const [soloFallidos, setSoloFallidos] = useState(false)
  /*
   * 🚨 «Sin foto o sin firma» era un número rojo y nada más.
   *
   * La oficina lo veía y no podía hacer nada: ni saber CUÁLES eran ni tocar
   * nada. Un rojo que no se puede resolver enseña a ignorar los rojos, y en el
   * piloto ese chip es lo primero que se mira.
   *
   * Ahora filtra, igual que el de al lado. Y desde ahí se puede devolver la
   * entrega para que el conductor mande la foto otra vez.
   */
  const [soloSinEvidencia, setSoloSinEvidencia] = useState(false)
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [revisando, setRevisando] = useState<Entrega | null>(null)
  /*
   * Lo que ya se decidió en esta tanda, para no volver a ofrecerlo antes de
   * que la lista se recargue.
   */
  const [revisadas, setRevisadas] = useState<Set<string>>(new Set())
  /** La entrega cuya evidencia se está mirando sin salir de la lista. */
  const [mirando, setMirando] = useState<Entrega | null>(null)
  /* A qué viaje trancado le estamos dando razón. */
  const [dandoRazon, setDandoRazon] = useState<NoEntregado | null>(null)
  /* Qué remesa está abierta en su ficha. */
  const [ficha, setFicha] = useState<string | null>(null)
  /*
   * 🚨 Aprobar VARIAS a la vez. Con 9 sin revisar se aguanta una por una; con
   * el piloto en marcha van a ser cientos al día.
   */
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set())
  const [aprobandoVarias, setAprobandoVarias] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)

  /**
   * 🚨 Recibe qué buscar en vez de leerlo del estado.
   *
   * Leerlo del estado tenía un fallo real: al tocar «Ver todas» se limpiaba el
   * texto y se recargaba de inmediato, pero la recarga todavía veía el texto
   * ANTERIOR y devolvía el resultado filtrado. Se vio probando — 1 entrega
   * donde había 4.
   */
  const cargar = useCallback(async (
    queDesde: string, queHasta: string, queBusca: string, queCliente = '', queEstado = '',
  ) => {
    setCargando(true)
    setError(null)
    try {
      const [r, n] = await Promise.all([
        pedirConSesion<{ entregas: Entrega[] }>(
          `/api/admin/entregas?desde=${queDesde}&hasta=${queHasta}` +
            `&busca=${encodeURIComponent(queBusca.trim())}` +
            `&cliente=${encodeURIComponent(queCliente)}&estado=${encodeURIComponent(queEstado)}`,
        ),
        // Los que no pudieron entregar siguen por días: es una alerta del
        // momento, no una consulta histórica.
        pedirConSesion<{ noEntregados: NoEntregado[] }>(
          `/api/admin/no-entregados?desde=${queDesde}&hasta=${queHasta}`,
        ),
      ])
      setEntregas(r.entregas)
      setNoEntregados(n.noEntregados)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar las entregas')
    } finally {
      setCargando(false)
    }
  }, [])

  // Los clientes para el filtro: solo los que tienen movimiento, no el
  // histórico entero. Se piden una vez.
  useEffect(() => {
    void pedirConSesion<{ clientes: typeof clientes }>('/api/admin/clientes-con-movimiento')
      .then((r) => setClientes(r.clientes))
      .catch(() => setClientes([]))
  }, [])

  useEffect(() => {
    void cargar(desde, hasta, busca, cliente, estado)
    // Solo el RANGO recarga solo. El texto se busca al enviar el formulario,
    // no en cada tecla: son 300 filas y el conductor de la oficina escribe
    // «UPO640» completo antes de querer el resultado.
  }, [desde, hasta, cliente, estado, cargar])

  /*
   * 🚨 UNA tabla con TODO. Luis: «¿dónde mierda se ven los que llegaron y no se
   * pudieron entregar? ¿Dónde se ven los estados en la tabla?»
   *
   * El error era mío y de fondo: los viajes que no pudieron entregar NO son
   * entregas —no existe fila en `entrega` para ellos— así que mandarlos a un
   * filtro sobre la tabla de entregas era mandarlos a un sitio donde nunca
   * podían aparecer. Aquí se mezclan las dos listas en una sola, ordenadas por
   * cuándo pasó, y **cada fila dice su estado**.
   */
  const sinOrden: FilaTabla[] = [
    ...(entregas ?? []).map((e): FilaTabla => ({ clase: 'entrega', cuando: e.entregada_at, e })),
    ...noEntregados.map((n): FilaTabla => ({ clase: 'fallido', cuando: n.ultimo_intento, n })),
  ]

  /*
   * 🚨 LAS DEL MISMO MANIFIESTO, SEGUIDAS. Lo pidió Luis:
   *
   * > «tenemos que buscar una manera de agrupar todas las remesas que hacen
   * > parte del mismo manifiesto ahí enseguida»
   *
   * Medido antes de esto: el manifiesto 000043831 tiene 3 remesas y ocupaba
   * **4 filas** —una ajena metida en el medio—; el 000043597 ocupaba 3 filas
   * por 2 remesas. Un camión con tres cargas se leía como tres viajes sueltos.
   *
   * ⭐ El orden general sigue siendo por fecha: cada manifiesto se ubica por la
   * entrega MÁS NUEVA de sus remesas, y dentro del grupo van por número. Así
   * lo de hoy sigue arriba y lo del mismo camión queda junto.
   */
  /* Los «no entregados» no traen manifiesto: se ordenan solo por su fecha. */
  const manifiestoDe = (f: FilaTabla): string =>
    (f.clase === 'entrega' ? f.e.numero_manifiesto : null) ?? ''
  const remesaDe = (f: FilaTabla): string => (f.clase === 'entrega' ? f.e.remesa : f.n.remesa)

  const masNuevaDelGrupo = new Map<string, string>()
  for (const f of sinOrden) {
    const m = manifiestoDe(f)
    if (!m) continue
    const actual = masNuevaDelGrupo.get(m) ?? ''
    if ((f.cuando ?? '') > actual) masNuevaDelGrupo.set(m, f.cuando ?? '')
  }
  const claveDeOrden = (f: FilaTabla): string => {
    const m = manifiestoDe(f)
    return m ? (masNuevaDelGrupo.get(m) ?? f.cuando ?? '') : (f.cuando ?? '')
  }

  const filas: FilaTabla[] = sinOrden.sort((a, b) => {
    const ca = claveDeOrden(a)
    const cb = claveDeOrden(b)
    if (ca !== cb) return ca < cb ? 1 : -1
    const ma = manifiestoDe(a)
    const mb = manifiestoDe(b)
    if (ma !== mb) return ma < mb ? 1 : -1
    return remesaDe(a) < remesaDe(b) ? -1 : 1
  })

  /** Cuántas filas comparten cada manifiesto, para poder marcarlo en la tabla. */
  const cuantasDelManifiesto = new Map<string, number>()
  for (const f of sinOrden) {
    const m = manifiestoDe(f)
    if (!m) continue
    cuantasDelManifiesto.set(m, (cuantasDelManifiesto.get(m) ?? 0) + 1)
  }

  const aLaVista = soloFallidos
    ? filas.filter((f) => f.clase === 'fallido')
    : soloSinEvidencia
      ? filas.filter(
          (f) => f.clase === 'entrega' && leFaltaLaHoja(f.e),
        )
      : filas

  const pag = usarPaginas(aLaVista)

  /*
   * Las que faltan por revisar, en el mismo orden en que se ven. De aquí sale
   * la siguiente cuando se aprueba o se devuelve una.
   */
  const porRevisar = aLaVista
    .filter((f): f is { clase: 'entrega'; cuando: string | null; e: Entrega } => f.clase === 'entrega')
    .map((f) => f.e)
    .filter((e) => faltaRevisarla(e) && !revisadas.has(e.entrega_id))

  /* Al cerrar el panel, la lista se pone al día de una vez. */
  function cerrarElPanel() {
    setRevisando(null)
    if (revisadas.size > 0) {
      setRevisadas(new Set())
      void cargar(desde, hasta, busca, cliente, estado)
    }
  }

  async function aprobarVarias() {
    if (aprobandoVarias || marcadas.size === 0) return
    setAprobandoVarias(true)
    setError(null)
    try {
      const r = await pedirConSesion<{ mensaje: string }>('/api/admin/entregas/aprobar-varias', {
        method: 'POST',
        body: JSON.stringify({ ids: [...marcadas] }),
      })
      setAviso(r.mensaje)
      setMarcadas(new Set())
      await cargar(desde, hasta, busca, cliente, estado)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron aprobar')
    } finally {
      setAprobandoVarias(false)
    }
  }

  const sinEvidencia = (entregas ?? []).filter(leFaltaLaHoja).length

  return (
    <div>
      <div className="tit-fila">
        <h1>Entregas</h1>
        <span>
          {entregas ? `${entregas.length} entre el ${comoSeLee(desde, desde)} y el ${comoSeLee(hasta, hasta)}` : 'Cargando…'}
        </span>
      </div>

      {/*
       * 🚨 UNA sola barra de filtros. Antes estaban regados en tres bloques
       * distintos —los botones de días arriba a la derecha, el cliente y el
       * estado en el medio, y el buscador aparte más abajo— y por eso Luis dijo
       * «esa manera de filtrar tan horrible».
       *
       * ⛔ Los filtros van al SERVIDOR, no sobre lo que ya llegó: la consulta
       * tiene tope de 300, así que filtrar en el navegador dejaría fuera lo que
       * no alcanzó a entrar y parecería que no existe.
       */}
      <form
        className="filtros-barra"
        onSubmit={(e) => {
          e.preventDefault()
          void cargar(desde, hasta, busca, cliente, estado)
        }}
      >
        {/*
         * ⛔⛔ LOS AVISOS VAN AQUÍ DENTRO, no como bloques encima.
         *
         * Estaban como dos cajas grandes arriba de los filtros y Luis lo cazó
         * de una: es el mismo error que odió en el tablero, «las alertas
         * arriba para que todo el contenido esté abajo». Empujaban los filtros
         * y la tabla fuera de la pantalla.
         *
         * Como chips son accionables —se tocan y filtran— y ocupan una línea.
         * La pantalla vuelve a ser: título → una caja → tabla.
         */}
        {(noEntregados.length > 0 || sinEvidencia > 0) && (
          <div className="avisos-chips">
            {noEntregados.length > 0 && (
              <button
                type="button"
                className={`chip-aviso ${soloFallidos ? 'chip-aviso-on' : ''}`}
                onClick={() => {
                  setSoloSinEvidencia(false)
                  setSoloFallidos((v) => !v)
                }}
              >
                <span className="chip-n num">{noEntregados.length}</span>
                llegaron y no pudieron entregar
                <em className="chip-porque">
                  {[...new Set(noEntregados.map((n) => MOTIVO_EN_PALABRAS[n.motivo ?? ''] ?? n.motivo))]
                    .filter(Boolean)
                    .slice(0, 2)
                    .join(' · ')}
                </em>
              </button>
            )}
            {sinEvidencia > 0 && (
              <button
                type="button"
                className={`chip-aviso chip-aviso-rojo ${soloSinEvidencia ? 'chip-aviso-on' : ''}`}
                onClick={() => {
                  setSoloFallidos(false)
                  setSoloSinEvidencia((v) => !v)
                }}
              >
                <span className="chip-n num">{sinEvidencia}</span>
                sin la hoja del cumplido
                <em className="chip-porque">Sin ella no se factura · toca para verlas</em>
              </button>
            )}
          </div>
        )}

        <div className="filtros-fila">
          <div className="campo">
            <label htmlFor="f-desde">Desde</label>
            <input id="f-desde" type="date" value={desde} max={hasta}
              onChange={(e) => setDesde(e.target.value)} />
          </div>
          <div className="campo">
            <label htmlFor="f-hasta">Hasta</label>
            <input id="f-hasta" type="date" value={hasta} min={desde} max={HOY_ISO()}
              onChange={(e) => setHasta(e.target.value)} />
          </div>
          <div className="campo campo-ancho">
            <label htmlFor="f-cliente">Cliente</label>
            <select id="f-cliente" value={cliente} onChange={(e) => setCliente(e.target.value)}>
              <option value="">Todos los clientes</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                  {c.en_calle > 0 ? ` — ${c.en_calle} en la calle` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="f-estado">En qué va</label>
            <select id="f-estado" value={estado} onChange={(e) => setEstado(e.target.value)}>
              <option value="">Todas</option>
              {/* Dice lo que hace: incluye las que ya vinieron corregidas. */}
              <option value="sin-revisar">Falta aprobarla</option>
              <option value="devueltas">Devueltas al conductor</option>
              <option value="aprobadas">Aprobadas</option>
              {/* 🚨 Sin la hoja no se le factura a la empresa matriz: es
                  trabajo del día, no una curiosidad. Medido el 24-ago-2026:
                  8 entregas de 61 sin una sola hoja. */}
              <option value="sin-hoja">Les falta la hoja del cumplido</option>
            </select>
          </div>
          <div className="campo campo-ancho">
            <label htmlFor="f-busca">Remesa, placa o cédula</label>
            <input id="f-busca" value={busca} onChange={(e) => setBusca(e.target.value)}
              placeholder="001087… · WGY482 · quién recibió" />
          </div>
          <button type="submit" className="btn-buscar">Buscar</button>
          {(cliente || estado || busca) && (
            <button
              type="button"
              className="btn-limpiar"
              onClick={() => {
                setCliente('')
                setEstado('')
                setBusca('')
                void cargar(desde, hasta, '')
              }}
            >
              Limpiar
            </button>
          )}
        </div>

        {(error || aviso || cargando) && (
          <p className={`linea-msg ${error ? 'linea-msg-mal' : aviso ? 'linea-msg-bien' : ''}`}>
            {error ?? aviso ?? 'Buscando…'}
          </p>
        )}

        {/* Los atajos, cada uno diciendo qué fechas aplica. */}
        <div className="atajos">
          {atajos().map((a) => {
            const puesto = desde === a.desde && hasta === a.hasta
            return (
              <button
                key={a.texto}
                type="button"
                className={`chip ${puesto ? 'chip-on' : ''}`}
                onClick={() => {
                  setDesde(a.desde)
                  setHasta(a.hasta)
                }}
              >
                {a.texto}
                <em>{comoSeLee(a.desde, a.hasta)}</em>
              </button>
            )
          })}
        </div>
      </form>

      {marcadas.size > 0 && puedeCambiar() && (
        <div className="masivo">
          <b className="num">
            {marcadas.size} {marcadas.size === 1 ? 'entrega marcada' : 'entregas marcadas'}
          </b>
          <button
            className="bmas"
            disabled={aprobandoVarias}
            onClick={() => void aprobarVarias()}
          >
            {aprobandoVarias
              ? 'Aprobando…'
              : `Aprobar ${marcadas.size === 1 ? 'esta entrega' : 'las ' + marcadas.size}`}
          </button>
          <span style={{ fontSize: 13, opacity: 0.8 }}>
            Para devolver una hay que hacerlo de a una, con su motivo.
          </span>
          {/*
            🚨 Con la clave compartida la aprobación queda SIN NOMBRE.
            Comprobado en la base: se aprobaron dos entregas y el registro dice
            «sin nombre». Si un cliente reclama por una entrega aprobada, hay
            que poder decir quién la aprobó — y si no se puede, la revisión no
            vale como control.
            Se dice AQUÍ, al lado del botón, no en una pantalla de sistema:
            este es el momento en que importa.
          */}
          {!quienEntro() && (
            <span className="masivo-sinnombre">
              Estás con la <b>clave compartida</b>: la aprobación queda sin tu nombre. Entra con tu
              correo y sí queda.
            </span>
          )}
          <button className="bmas" style={{ marginLeft: 'auto' }} onClick={() => setMarcadas(new Set())}>
            Quitar la selección
          </button>
        </div>
      )}

      {entregas !== null && aLaVista.length > 0 && (
        <>

          <div className="tabla-caja">
            <div className="tabla-scroll">
              <table>
                <thead>
                  <tr>
                    {puedeCambiar() && <th style={{ width: 36 }} aria-label="Seleccionar" />}
                    <th>Cuándo</th>
                    <th>Remesa</th>
                    <th>Camión</th>
                    <th>Conductor</th>
                    <th>Cliente y destino</th>
                    <th>Quién recibió</th>
                    <th>Evidencia</th>
                    {/* 🚨 La columna que faltaba. Luis: «¿dónde se ven los
                        estados en las entregas en la tabla?» */}
                    <th>Estado</th>
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {pag.enPantalla.map((f) =>
                    f.clase === 'fallido' ? (
                      <FilaFallido
                        key={`f-${f.n.remesa}`}
                        n={f.n}
                        alDarRazon={() => setDandoRazon(f.n)}
                        alVerFicha={() => setFicha(f.n.remesa)}
                      />
                    ) : (
                      <Fila
                        key={f.e.entrega_id}
                        e={f.e}
                        alRevisar={() => setRevisando(f.e)}
                        alVerFotos={() => setMirando(f.e)}
                        alVerFicha={() => setFicha(f.e.remesa)}
                        hermanas={cuantasDelManifiesto.get(f.e.numero_manifiesto ?? '') ?? 1}
                        marcada={marcadas.has(f.e.entrega_id)}
                        alMarcar={() =>
                          setMarcadas((antes) => {
                            const s2 = new Set(antes)
                            if (s2.has(f.e.entrega_id)) s2.delete(f.e.entrega_id)
                            else s2.add(f.e.entrega_id)
                            return s2
                          })
                        }
                      />
                    ),
                  )}
                </tbody>
              </table>
            </div>
            <PieDeTabla {...pag} queSon="entregas" />
          </div>
        </>
      )}

      {ficha && <Ficha numero={ficha} alCerrar={() => setFicha(null)} />}

      {dandoRazon && (
        <DarRazon
          n={dandoRazon}
          alCerrar={() => setDandoRazon(null)}
          alGuardar={() => {
            setDandoRazon(null)
            void cargar(desde, hasta, busca, cliente, estado)
          }}
        />
      )}

      {mirando && (
        <VerFotos
          entregaId={mirando.entrega_id}
          remesa={mirando.remesa}
          cliente={mirando.cliente}
          alCerrar={() => setMirando(null)}
          alRevisar={() => setRevisando(mirando)}
        />
      )}

      {revisando && (
        <Revisar
          e={revisando}
          quedan={porRevisar.length}
          alCerrar={cerrarElPanel}
          alListo={(mensaje) => {
            /*
             * 🚨 No se cierra: pasa a la SIGUIENTE sin revisar.
             *
             * Medido revisando de verdad: cada entrega son 1,8 segundos, pero
             * después de cada una hay que volver a la lista y buscar la
             * siguiente — y como las aprobadas se quedan en su fila, la
             * siguiente está cada vez más abajo. Con 30 pendientes eso es
             * media pantalla de desplazamiento por entrega.
             *
             * ⛔ La lista NO se recarga aquí: recargarla movería las filas
             * debajo del cursor. Se recarga al cerrar el panel.
             */
            setAviso(mensaje)
            setRevisadas((antes) => new Set(antes).add(revisando.entrega_id))
            const siguiente = porRevisar.find((x) => x.entrega_id !== revisando.entrega_id)
            setRevisando(siguiente ?? null)
            if (!siguiente) void cargar(desde, hasta, busca, cliente, estado)
          }}
        />
      )}
    </div>
  )
}

/**
 * Revisar una entrega: verla y decidir.
 *
 * 🚨 Muestra el cumplido completo dentro de la ventana. Decidir si una foto se
 * lee o si falta el sello **sin ver la foto** no es revisar, es adivinar.
 */
/**
 * 🚨 La evidencia, traída CON la sesión puesta.
 *
 * Un `<img src="/api/...">` no manda la cabecera de sesión: el servidor
 * respondería 401 y saldría el ícono de imagen rota. Es el mismo enredo del
 * archivo del manifiesto y el del visor de fotos.
 */
function usarEvidencia(entregaId: string) {
  const [piezas, setPiezas] = useState<{ id: string; tipo: string }[]>([])
  const [urls, setUrls] = useState<Record<string, string>>({})

  useEffect(() => {
    let vivo = true
    void (async () => {
      try {
        const r = await pedirConSesion<{ evidencia: { id: string; tipo: string }[] }>(
          `/api/admin/entregas/${entregaId}/evidencia`,
        )
        if (vivo) setPiezas(r.evidencia)
      } catch {
        // Sin evidencia el panel sigue sirviendo: se dice y ya.
      }
    })()
    return () => {
      vivo = false
    }
  }, [entregaId])

  useEffect(() => {
    let vivo = true
    const hechas: string[] = []
    void (async () => {
      for (const p of piezas) {
        try {
          const r = await fetch(`/api/admin/evidencia/${p.id}`, {
            headers: { Authorization: `Bearer ${tokenGuardado() ?? ''}` },
          })
          if (!r.ok) continue
          const u = URL.createObjectURL(await r.blob())
          hechas.push(u)
          if (vivo) setUrls((antes) => ({ ...antes, [p.id]: u }))
        } catch {
          // una foto que no abre no puede tumbar el panel entero
        }
      }
    })()
    return () => {
      vivo = false
      for (const u of hechas) URL.revokeObjectURL(u)
    }
  }, [piezas])

  return { piezas, urls }
}

function Revisar({
  e,
  quedan,
  alCerrar,
  alListo,
}: {
  e: Entrega
  /** Cuántas faltan por revisar, contando esta. */
  quedan: number
  alCerrar: () => void
  alListo: (mensaje: string) => void
}) {
  const { piezas, urls } = usarEvidencia(e.entrega_id)
  const [motivo, setMotivo] = useState<string | null>(null)
  const [observacion, setObservacion] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /*
   * 🚨 Al pasar a la siguiente hay que borrar lo escrito.
   *
   * Sin esto, el motivo y la nota de la entrega anterior quedarían escogidos
   * sobre la siguiente — y devolver una entrega con el motivo de otra es peor
   * que no revisarla.
   */
  useEffect(() => {
    setMotivo(null)
    setObservacion('')
    setError(null)
    setGuardando(false)
  }, [e.entrega_id])

  async function decidir(aprobar: boolean) {
    if (guardando) return
    if (!aprobar && !motivo) {
      setError('Escoge por qué se le devuelve')
      return
    }
    setGuardando(true)
    setError(null)
    try {
      const r = await pedirConSesion<{ mensaje: string }>(
        `/api/admin/entregas/${e.entrega_id}/revisar`,
        {
          method: 'POST',
          body: JSON.stringify({ aprobar, motivo: aprobar ? undefined : motivo, observacion }),
        },
      )
      alListo(r.mensaje)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar')
      setGuardando(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-6">
      <div className="w-full max-w-[1100px] rounded bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-[18px] font-semibold">
              Revisar la entrega de la remesa <span className="num">{e.remesa}</span>
            </h3>
            <p className="mt-0.5 text-[15px] text-tinta-suave">
              Recibió {e.recibe_nombre ?? '—'} · {e.cliente ?? 'Sin cliente'}
            </p>
          </div>
          <button onClick={alCerrar} className="text-[15px] font-medium text-tinta-suave">
            Cerrar
          </button>
        </div>

        {/*
          🚨 LA FOTO Y LA FIRMA, a la vista. Antes aquí iba el cumplido entero
          dentro de un marco de 420 px — y medido: el documento mide 1.305 px y
          **la foto arranca a los 756**. O sea que el analista abría el panel,
          veía el membrete y los datos, y aprobaba o devolvía sin haber visto la
          prueba, a menos que se acordara de bajar dentro del marco.
          Son las dos cosas por las que se decide: van primero y completas.
        */}
        {/*
          🚨 TODAS las hojas, no las dos primeras.
          Medido el 23-ago sobre una entrega real: el cumplido tenía **14
          hojas** y este panel pintaba **2**, con una linea abajo que decia
          «tiene 14 fotos en total». O sea que el analista aprobaba o devolvia
          un cumplido de 14 hojas habiendo visto dos — y la linea que se lo
          decia era justo la prueba de que el panel sabia lo que estaba
          escondiendo.

          Van en rejilla con su propio desplazamiento: los botones de decidir
          se quedan siempre a la vista, por muchas hojas que tenga.
        */}
        {piezas.length === 0 ? (
          <p className="mt-4 rounded border border-borde bg-[#FDF4E3] p-4 text-[15px] text-[#7D4A09]">
            Esta entrega no tiene fotos guardadas.
          </p>
        ) : (
          <div className="mt-4 max-h-[460px] overflow-y-auto rounded border border-borde p-2">
            <div className="grid gap-3 md:grid-cols-3">
              {piezas.map((p, i) => {
                const esFirma = p.tipo === 'FIRMA'
                const hojas = piezas.filter((x) => x.tipo !== 'FIRMA').length
                const cual = piezas.slice(0, i).filter((x) => x.tipo !== 'FIRMA').length + 1
                const nombre = esFirma
                  ? 'Firma de quien recibió'
                  : hojas > 1
                    ? `Hoja ${cual} de ${hojas}`
                    : 'Hoja del cumplido'
                return (
                  <figure key={p.id} className="m-0">
                    <figcaption className="etiqueta mb-1 text-[13px] uppercase tracking-wide text-tinta-suave">
                      {nombre}
                    </figcaption>
                    {urls[p.id] ? (
                      /* Se abre en grande en otra pestaña: una hoja escrita a
                         mano no siempre se lee en una miniatura. */
                      <a href={urls[p.id]} target="_blank" rel="noreferrer" title="Verla en grande">
                        <img
                          src={urls[p.id]}
                          alt={nombre}
                          className="max-h-[320px] w-full rounded border border-borde bg-white object-contain"
                        />
                      </a>
                    ) : (
                      <div className="flex h-[180px] items-center justify-center rounded border border-borde text-[15px] text-tinta-suave">
                        Cargando…
                      </div>
                    )}
                  </figure>
                )
              })}
            </div>
          </div>
        )}

        {/*
          🚨 A DÓNDE IBA y DÓNDE SE ENTREGÓ, uno al lado del otro.
          Sin esto, el motivo «el punto no cuadra con el destino» que ofrece
          esta misma pantalla no se puede juzgar: el analista tendría que
          abrir el cumplido, buscar el punto, buscar el destino y comparar de
          memoria. Aquí están los dos, y el punto se abre en el mapa.
        */}
        <div className="mt-3 grid gap-3 rounded border border-borde p-3 md:grid-cols-2">
          <div>
            <p className="etiqueta text-[13px] uppercase tracking-wide text-tinta-suave">
              A dónde iba
            </p>
            <p className="mt-0.5 text-[15px]">
              {e.destino ?? '—'}
              {e.ciudad_destino ? ` · ${e.ciudad_destino}` : ''}
            </p>
          </div>
          <div>
            <p className="etiqueta text-[13px] uppercase tracking-wide text-tinta-suave">
              Dónde se entregó
            </p>
            {hayPunto(e) ? (
              <p className="mt-0.5 text-[15px]">
                <a
                  href={`https://www.google.com/maps?q=${e.lat},${e.lng}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-navy underline"
                >
                  Ver el punto en el mapa
                </a>{' '}
                <span className="num text-[13px] text-tinta-suave">
                  {Number(e.lat).toFixed(5)}, {Number(e.lng).toFixed(5)}
                </span>
              </p>
            ) : (
              /* ⛔ Se dice, no se calla: si no hay punto, ese motivo no se
                 puede usar y el analista tiene que saberlo antes de escoger. */
              <p className="mt-0.5 text-[15px] text-[#7D4A09]">
                El celular no alcanzó a tomar el punto — por eso no se puede
                juzgar si cuadra con el destino.
              </p>
            )}
          </div>
        </div>

        {/* Y el cumplido entero sigue a un clic, para quien lo necesite. */}
        <p className="mt-2 text-[14px]">
          <a
            href={`/api/cumplido/${e.codigo_cumplido}`}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-navy underline"
          >
            Ver el cumplido completo
          </a>
          {piezas.length > 1 && (
            <span className="text-tinta-suave"> · {piezas.length} pruebas en total</span>
          )}
        </p>

        <div className="mt-5 grid gap-5 md:grid-cols-2">
          <div>
            <p className="text-[15px] font-semibold">Si algo está mal, ¿qué es?</p>
            <div className="mt-2 grid gap-1.5">
              {MOTIVOS_DEVOLUCION.map((m) => (
                <button
                  key={m.valor}
                  onClick={() => setMotivo(m.valor)}
                  className={`h-10 rounded px-3 text-left text-[14px] ${
                    motivo === m.valor
                      ? 'bg-navy font-semibold text-white'
                      : 'bg-[#EDEFF3] text-tinta'
                  }`}
                >
                  {m.texto}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col">
            <label htmlFor="obs" className="text-[15px] font-semibold">
              Nota para el conductor{' '}
              <span className="font-normal text-tinta-suave">(opcional)</span>
            </label>
            <textarea
              id="obs"
              value={observacion}
              onChange={(ev) => setObservacion(ev.target.value)}
              maxLength={500}
              placeholder="Por ejemplo: la foto salió movida, no se lee el número de la remesa"
              className="mt-2 flex-1 rounded border border-borde p-3 text-[15px]"
            />

            {error && <p className="mt-2 text-[15px] text-[#A4600D]">{error}</p>}

            {/*
              🚨 Cuántas faltan, dicho antes de decidir. Es lo que le dice al
              analista si va a estar aquí un minuto o media hora, y es la razón
              por la que el panel se queda abierto en vez de devolverlo a la
              lista después de cada una.
            */}
            {quedan > 1 && (
              <p className="mt-3 text-[15px] text-tinta-suave">
                Faltan <b className="font-semibold text-tinta">{quedan}</b> por revisar. Al decidir
                esta pasa sola a la siguiente.
              </p>
            )}

            {/*
              🚨🚨 SE DICE AQUÍ, PEGADO AL BOTÓN QUE DECIDE.
              
              El aviso existía solo en el bloque de aprobar varias a la vez, y
              **este es el sitio donde de verdad se decide**: medido el
              24-ago-2026, de 51 revisiones **50 quedaron sin nombre**, y no son
              históricas — dos son de hoy.
              
              Lo que eso significa cuando un cliente reclama por una entrega
              aprobada: no hay a quién preguntarle por qué se aprobó. Y una
              revisión de la que nadie responde no vale como control.
              
              ⛔ No frena nada ni molesta a quien ya entró con su correo: solo
              sale con la clave compartida, y dice cuál es la salida.
            */}
            {!quienEntro() && (
              <div className="mt-3 rounded-lg p-3" style={{ background: '#FDF4E3' }}>
                <p className="text-[15px] font-semibold" style={{ color: '#7D4A09' }}>
                  Esta decisión va a quedar sin tu nombre
                </p>
                <p className="mt-0.5 text-[13px] text-tinta-suave">
                  Entraste con la clave compartida de la oficina. Si un cliente reclama por esta
                  entrega, no habrá a quién preguntarle.
                </p>
                {/*
                  ⭐ Y SE LE DA LA SALIDA, no solo el consejo.
                  
                  Decir «sal y vuelve a entrar con tu correo» es pedirle trabajo
                  en medio de revisar 26 entregas — y nadie lo hace: medido, 50
                  de 51 revisiones quedaron sin nombre.
                  
                  Un clic. La pantalla de acceso abre con la puerta del correo,
                  así que no hay que escoger nada. Lo único que se pierde es la
                  nota si ya la escribió, y por eso se avisa.
                */}
                <button
                  onClick={() => {
                    /* Si ya escribió una nota, se le avisa antes de perderla. */
                    const seguro =
                      !observacion.trim() ||
                      window.confirm(
                        'Se va a cerrar la sesión para que entres con tu correo. La nota que escribiste se pierde. ¿Sigo?',
                      )
                    if (seguro) salirDeLaConsola()
                  }}
                  className="mt-2 h-9 rounded px-3 text-[14px] font-semibold"
                  style={{ background: '#7D4A09', color: 'white' }}
                >
                  Entrar con mi correo
                </button>
              </div>
            )}

            {/*
              🚨 Pegados abajo. Medido con un cumplido de 14 hojas: el panel
              creció y los dos botones quedaron FUERA de la pantalla — había
              que bajar a buscarlos después de mirar cada hoja. Pegados, se
              decide sin perderlos de vista por muchas hojas que traiga.
            */}
            <div className="sticky bottom-0 -mx-1 mt-3 flex gap-2 bg-white px-1 pb-1 pt-2">
              <button
                onClick={() => void decidir(true)}
                disabled={guardando}
                className="h-12 flex-1 rounded bg-[#2A8553] text-[16px] font-semibold text-white disabled:opacity-45"
              >
                {guardando ? 'Un momento…' : quedan > 1 ? 'Aprobar y seguir' : 'Está bien, aprobar'}
              </button>
              <button
                onClick={() => void decidir(false)}
                disabled={guardando}
                className="h-12 flex-1 rounded bg-[#FDECEC] text-[16px] font-semibold text-[#CA2B2B] disabled:opacity-45"
              >
                Devolvérsela
              </button>
            </div>

            {/*
              🚨 Se dice antes de tocar el botón: devolver NO borra la foto que
              ya mandó. El conductor SUMA una nueva.
            */}
            <p className="mt-2 text-[13px] text-tinta-suave">
              Al devolvérsela, el viaje le reaparece entre sus pendientes con el motivo. La foto que
              ya mandó no se borra: manda una nueva y quedan las dos.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * La fila de un viaje que LLEGÓ Y NO PUDO ENTREGAR.
 *
 * 🚨 Estos no tienen fila en `entrega`: el conductor llegó, no entregó y el
 * viaje sigue pendiente. Antes vivían en una tabla aparte y Luis preguntó
 * «¿dónde mierda se ven?» cuando la quité. Ahora son una fila más de la única
 * tabla, en rojo, con su motivo y cuántas veces lo intentó.
 */
/**
 * Darle razón al conductor, que está parado esperándola.
 *
 * 🚨 Faltaba y estaba MEDIDO: hay conductores que escribieron ellos mismos
 * «espero razón de la oficina» sobre novedades que **nunca le llegan al
 * cliente** —un camión varado es problema nuestro, no suyo— y a las que, por
 * tanto, nadie podía contestar.
 *
 * ⛔ Dar la razón NO cierra el viaje ni cambia su estado. Es una razón, no un
 * cierre: eso se cierra cuando la carga efectivamente se movió.
 */
/**
 * La historia completa de una remesa — tarea 3.4 del plan.
 *
 * 🚨 Hasta ahora todo lo que le pasa a una remesa estaba repartido: el intento
 * fallido en una pantalla, la respuesta del cliente en otra, la revisión en la
 * ventana de aprobar, y la hora exacta de cada cosa en ninguna. Para contestar
 * «¿qué pasó con esta remesa?» —que es como llama el cliente— tocaba armar el
 * cuento a mano entre tres pantallas.
 *
 * ⭐ Un solo hilo, en orden de reloj. A la izquierda los datos que no cambian;
 * a la derecha lo que fue pasando.
 */
function Ficha({ numero, alCerrar }: { numero: string; alCerrar: () => void }) {
  const [d, setD] = useState<FichaRemesa | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') alCerrar()
    }
    window.addEventListener('keydown', alTeclear)
    return () => window.removeEventListener('keydown', alTeclear)
  }, [alCerrar])

  useEffect(() => {
    let vivo = true
    pedirConSesion<FichaRemesa>(`/api/admin/remesa/${encodeURIComponent(numero)}`)
      .then((r) => vivo && setD(r))
      .catch((e) => vivo && setError(e instanceof Error ? e.message : 'No se pudo abrir'))
    return () => {
      vivo = false
    }
  }, [numero])

  const hitos = d ? armarHilo(d) : []

  return (
    <div className="visor-velo" onClick={alCerrar}>
      <div className="visor ficha" onClick={(e) => e.stopPropagation()}>
        <div className="visor-cab">
          <div>
            <b className="num">{numero}</b>
            <span>{d?.remesa.cliente ?? 'Historia de la remesa'}</span>
          </div>
          <button onClick={alCerrar} aria-label="Cerrar" className="visor-x">
            &times;
          </button>
        </div>

        {error && <p className="fi-vacio">{error}</p>}
        {!d && !error && <p className="fi-vacio">Abriendo…</p>}

        {d && (
          <div className="fi-cuerpo">
            <div className="fi-datos">
              {/* Los dos ejes de estado, separados: nunca se mezclan. */}
              <p className="fi-et">Estado de la remesa</p>
              <p className="fi-estado">{ESTADO_REMESA[d.remesa.estado_vulcano ?? ''] ?? '—'}</p>
              <p className="fi-nota">Lo dice el sistema de la empresa. Aquí solo se lee.</p>

              <p className="fi-et">Estado del viaje</p>
              <p className="fi-estado">
                {ESTADO_VIAJE[d.remesa.estado_evidencia ?? ''] ?? d.remesa.estado_evidencia}
              </p>

              <div className="fi-tabla">
                <Dato k="Camión" v={d.remesa.placa} num />
                <Dato
                  k="Conductor"
                  v={d.remesa.conductor_nombre ?? conPuntos(d.remesa.conductor_cedula ?? '')}
                  num={!d.remesa.conductor_nombre}
                />
                <Dato k="Manifiesto" v={d.remesa.numero_manifiesto} num />
                <Dato k="Pedido" v={d.remesa.pedido} num />
                {/* 🚨 Con pesoLegible, no a mano: la base devuelve `5.000` para
                    cinco toneladas y en Colombia eso se lee «cinco mil». */}
                <Dato k="Peso" v={pesoLegible(d.remesa.peso_ton)} num />
                <Dato k="Destino" v={d.remesa.destino} />
                <Dato k="Despachada" v={cuando(d.remesa.fecha_despacho)} num />
              </div>

              {!d.remesa.tiene_manifiesto && (
                <p className="fi-falta">Este viaje no tiene el manifiesto subido</p>
              )}
            </div>

            <div className="fi-hilo">
              <p className="fi-et">Qué ha pasado, en orden</p>
              {!hitos.length && <p className="fi-vacio">Todavía no hay movimientos.</p>}
              <ol className="hilo">
                {hitos.map((h, i) => (
                  <li key={i} className={h.clase}>
                    <div className="hilo-p" />
                    <div className="hilo-c">
                      <p className="hilo-t">{h.titulo}</p>
                      {h.detalle && <p className="hilo-d">{h.detalle}</p>}
                      {h.nota && <p className="hilo-n">«{h.nota}»</p>}
                      <p className="hilo-h num">
                        {cuando(h.cuando)}
                        {h.dondeDice ? ` · ${h.dondeDice}` : ''}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Dato({ k, v, num }: { k: string; v: string | number | null; num?: boolean }) {
  return (
    <>
      <span>{k}</span>
      <b className={num ? 'num' : undefined}>{v ?? '—'}</b>
    </>
  )
}

interface FichaRemesa {
  remesa: {
    numero: string
    numero_manifiesto: string | null
    placa: string | null
    conductor_cedula: string | null
    conductor_nombre: string | null
    peso_ton: string | number | null
    destino: string | null
    estado_vulcano: string | null
    estado_evidencia: string | null
    fecha_despacho: string | null
    cliente: string | null
    pedido: string | null
    tiene_manifiesto: boolean
  }
  eventos: {
    tipo: string
    ts_servidor: string | null
    ts_dispositivo: string | null
    desfase_segundos: number | null
    fotos: string | number
  }[]
  entregas: {
    recibe_nombre: string | null
    recibe_cedula: string | null
    entregada_at: string | null
    fotos: string | number
    firmas: string | number
  }[]
  novedades: {
    decision_cliente: string | null
    decision_nota: string | null
    decision_at: string | null
    quien_cliente: string | null
    razon_oficina: string | null
    razon_oficina_nota: string | null
    razon_oficina_at: string | null
    quien_oficina: string | null
  }[]
  revisiones: {
    resultado: string
    motivo_devolucion: string | null
    observacion: string | null
    creado_at: string | null
    analista: string | null
  }[]
}

interface Hito {
  cuando: string | null
  titulo: string
  detalle?: string | null
  nota?: string | null
  dondeDice?: string | null
  clase: string
}

/*
 * 🚨 Los dos estados vivos se llaman IGUAL en pantalla: «En la calle».
 *
 * El sistema de la empresa está renombrando uno por el otro —214 remesas ya
 * cambiaron— así que mostrarlos distinto haría que la misma carga pareciera
 * moverse sola de un estado a otro sin que pasara nada en la vía.
 */
const ESTADO_REMESA: Record<string, string> = {
  PLANILLADA: 'En la calle',
  EN_TRANSITO: 'En la calle',
  CUMPLIDO: 'Cumplida',
  FACTURADO: 'Facturada',
}

const ESTADO_VIAJE: Record<string, string> = {
  SIN_INICIAR: 'Por empezar',
  ACEPTADA: 'Aceptado por el conductor',
  EN_SITIO_CARGUE: 'En el cargue',
  CARGADA: 'Cargado',
  EN_TRANSITO: 'En camino',
  EN_SITIO_DESCARGUE: 'En el destino',
  ENTREGADA: 'Entregado',
  ENTREGADA_CON_NOVEDAD: 'Entregado con novedad',
  NO_ENTREGADA: 'No entregado',
  VALIDADA: 'Entregado y revisado',
  DEVUELTA: 'La prueba se le devolvió al conductor',
  DEVUELTA_A_PLANTA: 'La carga volvió a planta',
}

const PASO: Record<string, string> = {
  ACEPTO_VIAJE: 'El conductor aceptó el viaje',
  LLEGADA_CARGUE: 'Llegó al cargue',
  SALIDA_CARGUE: 'Salió del cargue',
  LLEGADA_DESCARGUE: 'Llegó al destino',
  ENTREGA_REGISTRADA: 'Registró la entrega',
  CUMPLIDO_FISICO_ADJUNTO: 'Le tomó foto al cumplido en papel',
  INTENTO_FALLIDO: 'Llegó y no pudo entregar',
  NOVEDAD_REPORTADA: 'Reportó una novedad',
  DEVUELTA_A_PLANTA: 'Devolvió la carga a planta',
  RETIRO_CONTENEDOR: 'Retiró el contenedor',
  DEVOLUCION_VACIO: 'Devolvió el vacío',
}

const QUE_PIDIERON: Record<string, string> = {
  REPROGRAMAR: 'que vuelva a intentar',
  CAMBIAR_DESTINO: 'que la entregue en otra dirección',
  DEVOLVER: 'que la devuelva a planta',
  COORDINO_YO: 'que él lo coordina y avisa',
  ESPERA: 'que espere ahí',
}

/**
 * Junta todo en un solo hilo ordenado por reloj.
 *
 * 🚨 Se ordena por la hora del CELULAR cuando la hay, no por la de acá: si el
 * conductor venía sin señal y subió tres cosas juntas al llegar a cobertura,
 * ordenarlas por la hora del servidor las pondría todas en el mismo minuto y en
 * un orden que nunca ocurrió.
 */
/** «1 foto», «3 fotos». El «(s)» se lee como un formulario, no como una frase. */
function cuantas(n: string | number, una: string, varias: string): string {
  const x = Number(n)
  return `${x} ${x === 1 ? una : varias}`
}

function armarHilo(d: FichaRemesa): Hito[] {
  const h: Hito[] = []

  if (d.remesa.fecha_despacho) {
    h.push({
      cuando: d.remesa.fecha_despacho,
      titulo: 'Despachada',
      detalle: 'Según el sistema de la empresa',
      clase: 'hi-gris',
    })
  }

  for (const e of d.eventos) {
    const desfase = Number(e.desfase_segundos ?? 0)
    h.push({
      cuando: e.ts_dispositivo ?? e.ts_servidor,
      titulo: PASO[e.tipo] ?? e.tipo,
      detalle: Number(e.fotos) > 0 ? `Con ${cuantas(e.fotos, 'foto', 'fotos')}` : null,
      /*
       * Si subió mucho después, decirlo. No es que reportara tarde: es que no
       * había señal, y quien mira la ficha tiene que poder distinguirlo.
       */
      dondeDice:
        desfase > 3600 ? `subido ${Math.round(desfase / 3600)} h después, sin señal` : null,
      clase: e.tipo === 'INTENTO_FALLIDO' || e.tipo === 'DEVUELTA_A_PLANTA' ? 'hi-mal' : 'hi-navy',
    })
  }

  for (const n of d.novedades) {
    if (n.decision_at && n.decision_cliente) {
      h.push({
        cuando: n.decision_at,
        titulo: `El cliente pidió ${QUE_PIDIERON[n.decision_cliente] ?? n.decision_cliente}`,
        detalle: n.quien_cliente ? `Lo respondió ${n.quien_cliente}` : null,
        nota: n.decision_nota,
        clase: 'hi-verde',
      })
    }
    if (n.razon_oficina_at && n.razon_oficina) {
      h.push({
        cuando: n.razon_oficina_at,
        titulo: `La oficina le dijo ${QUE_PIDIERON[n.razon_oficina] ?? n.razon_oficina}`,
        detalle: n.quien_oficina ? `La dio ${n.quien_oficina}` : null,
        nota: n.razon_oficina_nota,
        clase: 'hi-verde',
      })
    }
  }

  for (const e of d.entregas) {
    if (!e.entregada_at) continue
    const quien = e.recibe_nombre ? `Recibió ${e.recibe_nombre}` : 'Sin quién recibió'
    const ced = e.recibe_cedula ? ` · ${conPuntos(e.recibe_cedula)}` : ''
    h.push({
      cuando: e.entregada_at,
      titulo: 'Entregada',
      detalle:
        `${quien}${ced} · ${cuantas(e.fotos, 'foto', 'fotos')}` +
        ` y ${cuantas(e.firmas, 'firma', 'firmas')}`,
      clase: 'hi-verde',
    })
  }

  for (const v of d.revisiones) {
    const bien = v.resultado === 'APROBADA'
    h.push({
      cuando: v.creado_at,
      titulo: bien ? 'La oficina aprobó la prueba' : 'La oficina le devolvió la prueba al conductor',
      detalle: [
        /* 🚨 Si no se sabe quién fue, se DICE. Antes la línea desaparecía y
           el hueco quedaba invisible — justo lo que una auditoría busca. */
        v.analista ? `La revisó ${v.analista}` : 'No consta quién la revisó (clave compartida)',
        !bien && v.motivo_devolucion ? MOTIVO_DEVOLUCION[v.motivo_devolucion] : null,
      ]
        .filter(Boolean)
        .join(' · '),
      nota: v.observacion,
      clase: bien ? 'hi-verde' : 'hi-mal',
    })
  }

  return h.sort((a, b) => new Date(a.cuando ?? 0).getTime() - new Date(b.cuando ?? 0).getTime())
}

const MOTIVO_DEVOLUCION: Record<string, string> = {
  FOTO_ILEGIBLE: 'la foto no se leía',
  FALTA_SELLO: 'faltaba el sello',
  FIRMA_SIN_CEDULA: 'la firma iba sin cédula',
  GPS_FUERA_DE_RANGO: 'el punto quedó lejos',
  CANTIDAD_INCONSISTENTE: 'la cantidad no cuadraba',
  DOCUMENTO_EQUIVOCADO: 'era otro documento',
  FALTA_EIR: 'faltaba el EIR',
  OTRO: 'otro motivo',
}

function DarRazon({
  n,
  alCerrar,
  alGuardar,
}: {
  n: NoEntregado
  alCerrar: () => void
  alGuardar: () => void
}) {
  const [escogida, setEscogida] = useState<string | null>(n.razon_oficina)
  const [nota, setNota] = useState(n.razon_oficina_nota ?? '')
  const [mandando, setMandando] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') alCerrar()
    }
    window.addEventListener('keydown', alTeclear)
    return () => window.removeEventListener('keydown', alTeclear)
  }, [alCerrar])

  async function mandar() {
    if (!escogida || !n.novedad_id) return
    setMandando(true)
    setError('')
    try {
      await pedirConSesion(`/api/admin/novedades/${n.novedad_id}/razon`, {
        method: 'POST',
        body: JSON.stringify({ razon: escogida, nota }),
      })
      alGuardar()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo mandar la razón')
      setMandando(false)
    }
  }

  return (
    <div className="visor-velo" onClick={alCerrar}>
      <div className="visor" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 620 }}>
        <div className="visor-cab">
          <div>
            <b className="num">{n.remesa}</b>
            <span>Darle razón al conductor</span>
          </div>
          <button onClick={alCerrar} aria-label="Cerrar" className="visor-x">
            &times;
          </button>
        </div>

        <div className="dar-razon">
          {/* El contexto, para no tener que devolverse a la tabla a mirarlo. */}
          <p className="dr-caso">
            <b>{MOTIVO_EN_PALABRAS[n.motivo ?? ''] ?? n.motivo ?? 'No pudo entregar'}</b>
            {n.destino ? ` · ${n.destino}` : ''}
            {n.conductor_cedula ? ` · ${conPuntos(n.conductor_cedula)}` : ''}
          </p>
          {n.detalle && <p className="dr-detalle">«{n.detalle}»</p>}

          <p className="dr-tit">¿Qué tiene que hacer con la carga?</p>
          <div className="dr-ops">
            {RAZONES.map(([codigo, titulo, pie]) => (
              <button
                key={codigo}
                className={escogida === codigo ? 'sel' : ''}
                onClick={() => setEscogida(codigo)}
              >
                {titulo}
                <small>{pie}</small>
              </button>
            ))}
          </div>

          <label className="dr-tit" htmlFor="dr-nota">
            Lo que tenga que saber
          </label>
          <textarea
            id="dr-nota"
            maxLength={500}
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="La dirección buena, a qué hora abren, con quién preguntar…"
          />

          {error && <p className="dr-error">{error}</p>}

          <div className="dr-abajo">
            <span>Le llega al celular y le sirve sin señal. No cierra el viaje.</span>
            <button className="btn-tabla" onClick={alCerrar}>
              Cancelar
            </button>
            <button className="btn-tabla btn-tabla-fuerte" onClick={mandar} disabled={!escogida || mandando}>
              {mandando ? 'Mandando…' : 'Mandarle la razón'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function FilaFallido({
  n,
  alDarRazon,
  alVerFicha,
}: {
  n: NoEntregado
  alDarRazon: () => void
  alVerFicha: () => void
}) {
  const veces = Number(n.veces)
  return (
    <tr className="fila-fallida">
      {/* Sin casilla: un viaje que no entregó no se aprueba. */}
      {puedeCambiar() && <td />}
      <td className="num" style={{ whiteSpace: 'nowrap' }}>{cuando(n.ultimo_intento)}</td>

      <td>
        {/* El número abre la historia completa: es donde uno va a buscarla. */}
        <button className="num liga-remesa" onClick={alVerFicha}>
          {n.remesa}
        </button>
        {veces > 1 && <small style={{ color: '#CA2B2B', fontWeight: 600 }}>Lo intentó {veces} veces</small>}
      </td>

      <td className="num" style={{ whiteSpace: 'nowrap' }}>{n.placa ?? '—'}</td>
      {/* El nombre manda; la cedula queda como referencia debajo. */}
      <td style={{ whiteSpace: 'nowrap' }}>
        {n.conductor_nombre ? (
          <>
            <span>{n.conductor_nombre}</span>
            <small className="num">{conPuntos(n.conductor_cedula ?? '')}</small>
          </>
        ) : (
          <span className="num">{conPuntos(n.conductor_cedula ?? '')}</span>
        )}
      </td>

      <td>
        <span>{n.cliente ?? '—'}</span>
        <small>{n.destino ?? 'Sin destino'}</small>
      </td>

      {/* No recibió nadie y no hay evidencia: por eso las dos van vacías. */}
      <td style={{ color: '#8A94A6' }}>Nadie recibió</td>
      <td style={{ color: '#8A94A6' }}>—</td>

      <td>
        <span className="et et-mal">No pudo entregar</span>
        <small>{MOTIVO_EN_PALABRAS[n.motivo ?? ''] ?? n.motivo ?? 'Sin motivo'}</small>
        {/* Dónde quedó la carga según él. «Esperando razón nuestra» es lo que
            había que poder ver de un vistazo para saber a quién contestarle. */}
        {n.dijo_el_conductor && DIJO_EL_CONDUCTOR[n.dijo_el_conductor] && (
          <small>{DIJO_EL_CONDUCTOR[n.dijo_el_conductor]}</small>
        )}
      </td>

      {/*
        * 🚨 Aquí va lo que RESPONDIÓ EL CLIENTE.
        *
        * Antes decía siempre «Sigue pendiente», que no le servía a nadie: el
        * cliente contestaba en su portal y la oficina no se enteraba, así que
        * alguien seguía llamándolo por teléfono a preguntar lo mismo. Esta
        * columna es la instrucción con la que la oficina puede actuar.
        */}
      {/*
        * 🚨 Lo que YA se le dijo al conductor, y el botón para decirle algo.
        *
        * ⭐ Manda la más NUEVA, del cliente o nuestra, porque es la que el
        * conductor está viendo en su celular. Mostrar aquí una y allá otra
        * sería que la oficina crea que él tiene una instrucción distinta de la
        * que realmente le llegó.
        */}
      <td style={{ whiteSpace: 'nowrap' }}>
        {(() => {
          const nuestra = n.razon_oficina
            ? { que: n.razon_oficina, nota: n.razon_oficina_nota, cuando: n.razon_oficina_at }
            : null
          const suya = n.decision_cliente
            ? { que: n.decision_cliente, nota: n.decision_nota, cuando: n.decision_at }
            : null
          const manda =
            nuestra && suya
              ? new Date(nuestra.cuando ?? 0) >= new Date(suya.cuando ?? 0)
                ? { ...nuestra, quien: 'Se lo dijimos nosotros' }
                : { ...suya, quien: 'Lo pidió el cliente' }
              : nuestra
                ? { ...nuestra, quien: 'Se lo dijimos nosotros' }
                : suya
                  ? { ...suya, quien: 'Lo pidió el cliente' }
                  : null

          if (manda) {
            return (
              <>
                <span className="et et-ok">
                  {RAZONES.find((r) => r[0] === manda.que)?.[1] ??
                    PIDIO_EL_CLIENTE[manda.que] ??
                    manda.que}
                </span>
                <small>
                  {manda.quien} · {cuando(manda.cuando)}
                </small>
                {manda.nota && (
                  <small style={{ whiteSpace: 'normal', maxWidth: 260 }}>«{manda.nota}»</small>
                )}
                {puedeCambiar() && n.novedad_id && (
                  <button className="btn-tabla" onClick={alDarRazon} style={{ marginTop: 4 }}>
                    Cambiar la razón
                  </button>
                )}
              </>
            )
          }
          return (
            <>
              <span className="et et-esp">
                {n.espera_cliente ? 'Esperando al cliente' : 'Sin razón todavía'}
              </span>
              <small>
                {n.espera_cliente
                  ? 'Se le preguntó en su portal'
                  : 'A esta no se le pregunta al cliente'}
              </small>
              {puedeCambiar() && n.novedad_id && (
                <button className="btn-tabla" onClick={alDarRazon} style={{ marginTop: 4 }}>
                  Darle razón
                </button>
              )}
            </>
          )
        })()}
      </td>
    </tr>
  )
}

function Fila({
  e,
  alRevisar,
  alVerFotos,
  alVerFicha,
  marcada,
  alMarcar,
  hermanas = 1,
}: {
  e: Entrega
  alRevisar: () => void
  alVerFotos: () => void
  alVerFicha: () => void
  marcada: boolean
  alMarcar: () => void
  /** Cuántas remesas comparten el manifiesto de esta, contándola. */
  hermanas: number
}) {
  const fotos = Number(e.tiene_foto)
  const foto = fotos > 0
  const firma = Number(e.tiene_firma) > 0
  const hojas = Number(e.hojas_papel)
  /* Completa = tiene la hoja del cumplido. La firma dejó de pedirse el 22-ago. */
  const completa = foto

  /*
   * 🚨 Las clases sueltas de antes pintaban gris sobre gris. Luis: «ahora la
   * tabla me la dejaste gris con letras gris». Ahora el dato principal va en
   * tinta y el secundario en <small>, que la caja de la tabla ya estiliza.
   */
  return (
    <tr>
      {/* Solo se puede marcar lo que está sin revisar: aprobar dos veces la
          misma entrega no cambia nada, pero confunde al contar. */}
      {puedeCambiar() && (
        <td>
          {!e.revision && (
            <input type="checkbox" aria-label={`Marcar ${e.remesa}`} checked={marcada} onChange={alMarcar} />
          )}
        </td>
      )}
      <td className="num" style={{ whiteSpace: 'nowrap' }}>{cuando(e.entregada_at)}</td>

      <td>
        <button className="num liga-remesa" onClick={alVerFicha}>
          {e.remesa}
        </button>
        {e.numero_manifiesto && <small className="num">Man. {e.numero_manifiesto}</small>}
        {/*
          🚨 Cuántas remesas van en ese mismo manifiesto.
          Medido: un manifiesto SIEMPRE lo lleva un solo conductor en un solo
          camión —5.078 de 5.078— así que varias remesas del mismo manifiesto
          son UNA salida con varias cargas. Sin esta marca se leían como viajes
          sueltos, y la oficina no sabía que era el mismo camión.
        */}
        {hermanas > 1 && (
          <small className="et et-neu" style={{ marginTop: 2 }}>
            va con {hermanas - 1} más del manifiesto
          </small>
        )}
      </td>

      <td className="num" style={{ whiteSpace: 'nowrap' }}>{e.placa ?? '—'}</td>

      <td style={{ whiteSpace: 'nowrap' }}>
        {e.conductor_nombre ? (
          <>
            <span>{e.conductor_nombre}</span>
            <small className="num">{conPuntos(e.conductor_cedula ?? '')}</small>
          </>
        ) : (
          <span className="num">{conPuntos(e.conductor_cedula ?? '')}</span>
        )}
      </td>

      <td>
        <span>{e.cliente ?? '—'}</span>
        {/*
         * 🚨 La ciudad primero. Sin ella, «Blocport Bodega 46» no le dice a
         * nadie en la oficina si eso queda en Cartagena o en Santa Marta.
         */}
        <small>
          {e.ciudad_destino && (
            <span style={{ fontWeight: 600, color: '#121B2B' }}>{e.ciudad_destino}</span>
          )}
          {e.ciudad_destino && e.destino ? ' · ' : ''}
          {e.destino ?? (e.ciudad_destino ? '' : 'Sin destino')}
        </small>
        {e.observacion && (
          <small style={{ color: '#A4600D', fontWeight: 600 }}>Novedad: {e.observacion}</small>
        )}
      </td>

      <td>
        <span>{e.recibe_nombre ?? '—'}</span>
        {e.recibe_cedula && <small className="num">{conPuntos(e.recibe_cedula)}</small>}
      </td>

      <td>
        {/*
         * 🚨 Aquí se ABRE la evidencia. Antes había que entrar al panel de
         * revisión completo solo para mirar una foto; Luis pidió poder verlas
         * «sin salir de la lista».
         */}
        {completa ? (
          <>
            {/* Se dice CUANTAS hojas trae: un cumplido puede ser un fajo, y
                desde la lista hay que poder ver si vinieron todas. */}
            <button onClick={alVerFotos} className="et et-ok et-boton" title="Ver las hojas">
              {fotos > 1 ? `Ver las ${fotos} hojas` : 'Ver la hoja'}
            </button>
            {hojas > 0 && (
              <small>
                {hojas} {hojas === 1 ? 'hoja agregada después' : 'hojas agregadas después'}
              </small>
            )}
          </>
        ) : firma ? (
          <button onClick={alVerFotos} className="et et-mal et-boton" title="Ver lo que hay">
            Solo la firma
          </button>
        ) : hojas > 0 ? (
          /* La entrega no trae hoja, pero el conductor la mandó DESPUÉS: la
             remesa sí tiene su cumplido. Decir «sin foto» aquí sería marcar en
             rojo algo que ya está resuelto. */
          <button onClick={alVerFotos} className="et et-ok et-boton" title="Ver las hojas">
            {hojas === 1 ? 'La hoja llegó después' : `${hojas} hojas llegaron después`}
          </button>
        ) : (
          <span className="et et-mal">Sin la hoja del cumplido</span>
        )}
      </td>

      {/*
        🚨 EL ESTADO, en una etiqueta de color. Antes esta columna solo tenía
        botones y no se sabía en qué iba cada entrega.

        Corregida va PRIMERO en el orden de decisión: una entrega que el
        conductor ya corrigió no puede seguir mostrándose como «Devuelta»,
        quedaría marcada así para siempre y nadie la volvería a mirar.
      */}
      <td>
        {e.revision === 'DEVUELTA' && e.corregida_despues ? (
          <span className="et et-esp">Corregida, por revisar</span>
        ) : e.revision === 'APROBADA' ? (
          <span className="et et-ok">Aprobada</span>
        ) : e.revision === 'DEVUELTA' ? (
          <>
            <span className="et et-esp">Devuelta</span>
            <small>
              {MOTIVOS_DEVOLUCION.find((m) => m.valor === e.motivo_devolucion)?.texto ??
                e.motivo_devolucion}
            </small>
          </>
        ) : (
          <span className="et et-neu">Sin revisar</span>
        )}
      </td>

      <td style={{ whiteSpace: 'nowrap' }}>
        {e.revision !== 'APROBADA' && puedeCambiar() && (
          <button onClick={alRevisar} className="btn-tabla">
            {e.revision === 'DEVUELTA' && e.corregida_despues ? 'Revisar otra vez' : 'Revisar'}
          </button>
        )}
        <a
          href={`/api/cumplido/${e.codigo_cumplido}`}
          target="_blank"
          rel="noopener"
          className="btn-tabla btn-tabla-fuerte"
          style={{ marginLeft: 6 }}
        >
          Ver
        </a>
      </td>
    </tr>
  )
}

/** «20 ago, 1:11 p. m.» — corto, porque va en una columna de tabla. */
function cuando(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('es-CO', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d)
}

/** La cédula con puntos se lee; sin puntos, no. */
function conPuntos(cedula: string): string {
  const n = Number(cedula)
  return Number.isFinite(n) && cedula ? new Intl.NumberFormat('es-CO').format(n) : (cedula ?? '—')
}
